// src/components/billing/SubscriptionPlans.js
import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";
import { ConfirmationModal } from "../ui/ConfirmationModal";
import { apiService } from "../../services/api";
import toast from "react-hot-toast";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  SparklesIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

const DEFAULT_FEATURE_FLAGS = {
  allow_custom_rating_scales: true,
  allow_parent_reports: true,
  allow_team_builder: true,
  allow_csv_import: true,
  allow_teamsnap_import: false,
  allow_sportsengine_import: false,
  allow_custom_branding: false,
};

const FEATURE_FLAG_LABELS = {
  allow_custom_rating_scales: "Custom Rating Scales",
  allow_parent_reports: "Parent Report Emails",
  allow_team_builder: "Team Builder",
  allow_csv_import: "CSV Import",
  allow_teamsnap_import: "TeamSnap Import",
  allow_sportsengine_import: "SportsEngine Import",
  allow_custom_branding: "Custom Report Branding",
};

const emptyForm = {
  name: "",
  description: "",
  price_per_player: "",
  max_players: "",
  max_staff: "",
  max_evaluations: "",
  features: [],
  is_active: true,
  highlight: false,
  price_display: "",
  price_period: "",
  cta_text: "Contact Us",
  cta_link: "/portal/login",
  sort_order: "0",
  feature_flags: { ...DEFAULT_FEATURE_FLAGS },
};

function parseFeatures(features) {
  if (Array.isArray(features)) return features;
  if (typeof features === "string") {
    try {
      return JSON.parse(features);
    } catch {
      return [];
    }
  }
  return [];
}

// PlanForm is defined at module level so React never unmounts it on re-render
function PlanForm({
  isEdit,
  formData,
  setFormData,
  newFeature,
  setNewFeature,
  loading,
  onSubmit,
  onCancel,
}) {
  const setField = (key) => (e) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, newFeature.trim()],
      }));
      setNewFeature("");
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleEditFeature = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.map((f, i) => (i === index ? value : f)),
    }));
  };

  const handleMoveFeature = (index, direction) => {
    setFormData((prev) => {
      const features = [...prev.features];
      const target = index + direction;
      if (target < 0 || target >= features.length) return prev;
      [features[index], features[target]] = [features[target], features[index]];
      return { ...prev, features };
    });
  };

  const handleFeatureKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddFeature();
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Name + Internal price */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Plan Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={setField("name")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
            placeholder="e.g., Starter, Basic, Elite"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price per Player (internal billing)
          </label>
          <div className="relative">
            <CurrencyDollarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="number"
              step="0.01"
              value={formData.price_per_player}
              onChange={setField("price_per_player")}
              className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={setField("description")}
          rows={2}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
          placeholder="Brief description shown on the pricing page..."
        />
      </div>

      {/* Marketing display fields */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
        <h4 className="text-sm font-semibold text-gray-700">
          Marketing Display (Pricing Page)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price Display
            </label>
            <input
              type="text"
              value={formData.price_display}
              onChange={setField("price_display")}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
              placeholder='e.g., "Free" or "$7"'
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price Period
            </label>
            <input
              type="text"
              value={formData.price_period}
              onChange={setField("price_period")}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
              placeholder='e.g., "/per player /per evaluation"'
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CTA Button Text
            </label>
            <input
              type="text"
              value={formData.cta_text}
              onChange={setField("cta_text")}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
              placeholder='e.g., "Get Started" or "Contact Us"'
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CTA Button Link
            </label>
            <input
              type="text"
              value={formData.cta_link}
              onChange={setField("cta_link")}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
              placeholder="/portal/login"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sort Order
            </label>
            <input
              type="number"
              value={formData.sort_order}
              onChange={setField("sort_order")}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
              placeholder="0"
            />
          </div>
          <div className="flex items-center pt-6">
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  highlight: !prev.highlight,
                }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.highlight ? "bg-sky-500" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.highlight ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className="ml-3 text-sm text-gray-700">
              {formData.highlight
                ? "Highlighted (Most Popular)"
                : "Not highlighted"}
            </span>
          </div>
        </div>
      </div>

      {/* Plan limits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Max Players
          </label>
          <input
            type="number"
            value={formData.max_players}
            onChange={setField("max_players")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
            placeholder="Unlimited"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Max Staff
          </label>
          <input
            type="number"
            value={formData.max_staff}
            onChange={setField("max_staff")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
            placeholder="Unlimited"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Max Evaluations/yr
          </label>
          <input
            type="number"
            value={formData.max_evaluations}
            onChange={setField("max_evaluations")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
            placeholder="Unlimited"
          />
        </div>
      </div>

      {/* Features */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Included Features (shown on pricing page)
        </label>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newFeature}
            onChange={(e) => setNewFeature(e.target.value)}
            onKeyPress={handleFeatureKeyPress}
            placeholder="Add a feature..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
          />
          <Button
            type="button"
            onClick={handleAddFeature}
            disabled={
              !newFeature.trim() ||
              formData.features.includes(newFeature.trim())
            }
            size="sm"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
        {formData.features.length > 0 ? (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {formData.features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 rounded-lg border"
              >
                {/* Reorder buttons */}
                <div className="flex flex-col flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleMoveFeature(index, -1)}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed"
                  >
                    <ChevronUpIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveFeature(index, 1)}
                    disabled={index === formData.features.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed"
                  >
                    <ChevronDownIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
                <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => handleEditFeature(index, e.target.value)}
                  className="flex-1 text-sm text-gray-700 bg-transparent border-0 outline-none focus:ring-1 focus:ring-slate-300 rounded px-1 py-0.5 min-w-0"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveFeature(index)}
                  className="text-red-500 hover:text-red-700 p-1 rounded flex-shrink-0"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-sm">No features added yet</p>
          </div>
        )}
      </div>

      {/* Feature Access Flags */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
        <h4 className="text-sm font-semibold text-gray-700">Feature Access</h4>
        <p className="text-xs text-gray-500">
          Toggle which features are available on this plan. Disabled features return a 402 error prompting the club to upgrade.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.keys(FEATURE_FLAG_LABELS).map((flagKey) => (
            <div key={flagKey} className="flex items-center">
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    feature_flags: {
                      ...prev.feature_flags,
                      [flagKey]: !prev.feature_flags[flagKey],
                    },
                  }))
                }
                className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                  formData.feature_flags[flagKey] ? "bg-sky-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    formData.feature_flags[flagKey] ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </button>
              <span className="ml-2 text-sm text-gray-700">
                {FEATURE_FLAG_LABELS[flagKey]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Active toggle */}
      <div className="flex items-center">
        <button
          type="button"
          onClick={() =>
            setFormData((prev) => ({ ...prev, is_active: !prev.is_active }))
          }
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            formData.is_active ? "bg-slate-600" : "bg-gray-200"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              formData.is_active ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
        <span className="ml-3 text-sm text-gray-700">
          {formData.is_active ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={loading}>
          {isEdit ? "Update Plan" : "Create Plan"}
        </Button>
      </div>
    </form>
  );
}

const SubscriptionPlans = ({ plans, onUpdate }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState(emptyForm);
  const [newFeature, setNewFeature] = useState("");

  const resetForm = () => {
    setFormData(emptyForm);
    setNewFeature("");
  };

  const buildPayload = () => ({
    name: formData.name,
    description: formData.description,
    price_per_player_per_year: parseFloat(formData.price_per_player) || 0,
    max_players_per_club: formData.max_players
      ? parseInt(formData.max_players)
      : null,
    max_staff_per_club: formData.max_staff
      ? parseInt(formData.max_staff)
      : null,
    max_evaluations_per_year: formData.max_evaluations
      ? parseInt(formData.max_evaluations)
      : null,
    features: formData.features.filter((f) => f.trim()),
    feature_flags: formData.feature_flags,
    active: formData.is_active,
    highlight: formData.highlight,
    price_display: formData.price_display,
    price_period: formData.price_period,
    cta_text: formData.cta_text,
    cta_link: formData.cta_link,
    sort_order: parseInt(formData.sort_order) || 0,
  });

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiService.createSubscriptionPlan(buildPayload());
      toast.success("Subscription plan created successfully!");
      setShowCreateModal(false);
      resetForm();
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to create subscription plan");
    }
    setLoading(false);
  };

  const handleUpdatePlan = async (e) => {
    e.preventDefault();
    if (!selectedPlan) return;
    setLoading(true);
    try {
      await apiService.updateSubscriptionPlan(selectedPlan.id, buildPayload());
      toast.success("Subscription plan updated successfully!");
      setShowEditModal(false);
      setSelectedPlan(null);
      resetForm();
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to update subscription plan");
    }
    setLoading(false);
  };

  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    setDeleting(true);
    try {
      await apiService.deleteSubscriptionPlan(planToDelete.id);
      toast.success("Subscription plan deleted successfully!");
      setShowDeleteModal(false);
      setPlanToDelete(null);
      onUpdate?.();
    } catch (error) {
      toast.error(error?.message || "Failed to delete subscription plan");
    }
    setDeleting(false);
  };

  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const handleEditClick = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || "",
      price_per_player: plan.price_per_player_per_year?.toString() || "",
      max_players: plan.max_players_per_club?.toString() || "",
      max_staff: plan.max_staff_per_club?.toString() || "",
      max_evaluations: plan.max_evaluations_per_year?.toString() || "",
      features: parseFeatures(plan.features),
      is_active: plan.active ?? true,
      highlight: plan.highlight ?? false,
      price_display: plan.price_display || "",
      price_period: plan.price_period || "",
      cta_text: plan.cta_text || "Contact Us",
      cta_link: plan.cta_link || "/portal/login",
      sort_order: plan.sort_order?.toString() || "0",
      feature_flags: plan.feature_flags
        ? { ...DEFAULT_FEATURE_FLAGS, ...plan.feature_flags }
        : { ...DEFAULT_FEATURE_FLAGS },
    });
    setShowEditModal(true);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);

  const formProps = {
    formData,
    setFormData,
    newFeature,
    setNewFeature,
    loading,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Subscription Plans
          </h2>
          <p className="text-gray-600">Manage pricing plans for clubs</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Plan
        </Button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className="relative">
            {plan.highlight && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge variant="blue" className="px-3 py-1">
                  <SparklesIcon className="w-3 h-3 mr-1" />
                  Most Popular
                </Badge>
              </div>
            )}

            <CardHeader className="text-center pb-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  {plan.description && (
                    <p className="text-gray-600 text-sm mt-1">
                      {plan.description}
                    </p>
                  )}
                </div>
                <Badge variant={plan.active ? "green" : "gray"}>
                  {plan.active ? "Active" : "Inactive"}
                </Badge>
              </div>

              <div className="mt-4">
                <div className="text-3xl font-bold text-gray-900">
                  {plan.price_display ||
                    formatCurrency(plan.price_per_player_per_year)}
                </div>
                <div className="text-sm text-gray-500">
                  {plan.price_period || "per player / evaluation"}
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {/* Limits + Sort */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Max Players:</span>
                  <span className="font-medium">
                    {plan.max_players_per_club || "∞"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Max Evaluations/yr:</span>
                  <span className="font-medium">
                    {plan.max_evaluations_per_year || "∞"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Max Staff:</span>
                  <span className="font-medium">
                    {plan.max_staff_per_club || "∞"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Sort Order:</span>
                  <span className="font-medium">{plan.sort_order ?? 0}</span>
                </div>
              </div>

              {/* Feature Flags Summary */}
              {plan.feature_flags && (
                <div className="mb-4 p-3 border border-gray-200 rounded-lg">
                  <h5 className="text-xs font-medium text-gray-700 mb-2">Feature Access</h5>
                  <div className="grid grid-cols-1 gap-1">
                    {Object.entries(FEATURE_FLAG_LABELS).map(([key, label]) => {
                      const enabled = plan.feature_flags?.[key] !== false;
                      return (
                        <div key={key} className="flex items-center justify-between text-xs">
                          <span className="text-gray-600">{label}</span>
                          <span className={`font-medium ${enabled ? "text-green-600" : "text-red-500"}`}>
                            {enabled ? "✓" : "✗"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Features */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Features:
                </h4>
                <div className="space-y-2">
                  {parseFeatures(plan.features)
                    .slice(0, 5)
                    .map((feature, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <CheckIcon className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  {parseFeatures(plan.features).length > 5 && (
                    <div className="text-xs text-gray-500">
                      +{parseFeatures(plan.features).length - 5} more features
                    </div>
                  )}
                </div>
              </div>

              {/* Usage Stats */}
              <div className="mb-4 p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <UserGroupIcon className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-sm text-gray-600">Active Clubs</span>
                  </div>
                  <span className="font-medium">
                    {plan.active_clubs_count || 0}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEditClick(plan)}
                  className="flex-1"
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleDeleteClick(plan)}
                  disabled={plan.active_clubs_count > 0}
                >
                  <TrashIcon className="w-4 h-4" />
                </Button>
              </div>

              {plan.active_clubs_count > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Cannot delete: {plan.active_clubs_count} clubs using this plan
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {plans.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No subscription plans
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first subscription plan.
            </p>
            <div className="mt-6">
              <Button onClick={() => setShowCreateModal(true)}>
                <PlusIcon className="w-4 h-4 mr-2" />
                Create Plan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Plan Modal */}
      <Modal
        isOpen={showCreateModal}
        title="Create Subscription Plan"
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        size="lg"
      >
        <PlanForm
          {...formProps}
          isEdit={false}
          onSubmit={handleCreatePlan}
          onCancel={() => {
            setShowCreateModal(false);
            resetForm();
          }}
        />
      </Modal>

      {/* Edit Plan Modal */}
      <Modal
        isOpen={showEditModal}
        title="Edit Subscription Plan"
        onClose={() => {
          setShowEditModal(false);
          setSelectedPlan(null);
          resetForm();
        }}
        size="lg"
      >
        <PlanForm
          {...formProps}
          isEdit={true}
          onSubmit={handleUpdatePlan}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
            resetForm();
          }}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setPlanToDelete(null);
        }}
        onConfirm={handleDeletePlan}
        title="Delete Subscription Plan"
        message={
          planToDelete
            ? `Are you sure you want to delete "${planToDelete.name}"? This action cannot be undone.`
            : "Are you sure you want to delete this plan?"
        }
        confirmText="Delete Plan"
        loading={deleting}
      />
    </div>
  );
};

export default SubscriptionPlans;
